import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { wizardSchema, defaultWizardValues, WizardFormData } from '../schemas';
import useApi from '../../../hooks/useApi';
import { showMessage } from '../../utils/showMessage';
import { useWizardData } from './useWizardData';
import { v4 } from 'uuid';
import { appendUidToUrl, buildRLink } from '../utils/domains';

export function useWizardForm(onNavigateToSuccess?: () => void) {
  const form = useForm<WizardFormData>({
    resolver: zodResolver(wizardSchema),
    defaultValues: defaultWizardValues,
    mode: 'onChange',
  });

  const { domains } = useWizardData();

  const { watch, setValue, trigger } = form;

  const [submissionResult, setSubmissionResult] = useState<any>(null);

  const { api: apiClient, createApiCall } = useApi();

  const submitWizard = createApiCall(async ({ signal }, wizardData: WizardFormData) => {
    const response = await apiClient.post('/api/offers/wizard', wizardData, { signal });
    return response.data;
  });

  const offerName = watch('offerName');
  const offerRedirectDomain = watch('redirectDomainId');
  const offerUniqueId = watch('offerUniqueId');

  useEffect(() => {
    setValue('offerUniqueId', v4());
  }, []);

  useEffect(() => {
    setValue('campaignName', `${offerName} Campaign`);
    setValue('trafficSourceName', `${offerName} Source`);
  }, [offerName, setValue]);

  const getRedirectUrl = useCallback(
    (offerRedirectDomainId: string, offerUniqueId: string) => {
      const offerDomain = domains.find((domain) => domain._id === offerRedirectDomainId);

      if (!offerDomain) return '';

      const isOnlydrops = offerDomain.domainName.startsWith('onlydrops');

      let affUrl = offerDomain.domainLink;

      if (isOnlydrops) {
        affUrl = appendUidToUrl(affUrl, offerUniqueId);
        return affUrl;
      }

      return buildRLink(affUrl, offerUniqueId);
    },
    [domains]
  );

  useEffect(() => {
    if (!offerRedirectDomain) return;

    const redirectUrl = getRedirectUrl(offerRedirectDomain, offerUniqueId);

    setValue('affiliateUrl', redirectUrl);
  }, [getRedirectUrl, offerRedirectDomain, offerUniqueId]);

  const validateStep = async (step: number): Promise<boolean> => {
    const offerStep = step === 0;
    const trafficSourceStep = step === 1;
    const campaignStep = step === 2;

    const network = watch('network');

    const autoCreateTrafficSource = watch('autoCreateTrafficSource');
    const autoCreateCampaign = watch('autoCreateCampaign');

    const validateNewSource = trafficSourceStep && autoCreateTrafficSource;
    const validateExistingSource = trafficSourceStep && !autoCreateTrafficSource;

    const validateNewCampaign = campaignStep && autoCreateCampaign;
    const validateExistingCampaign = campaignStep && !autoCreateCampaign;

    const offerTriggerFields = ['offerName', 'offerLink', 'entryDomainId', 'redirectDomainId'] as const;
    const trafficSourceCommonFields = ['trafficSourceName', 'network', 'x1Parameters'] as const;

    const campaignTriggerFields = ['campaignName', 'campaignEntryDomainId', 'offerGroupId'] as const;

    const campaignOfferSettingsFields = ['merchantId', 'merchantName', 'merchantUrl', 'affiliateUrl', 'clickCap', 'commission', 'geo', 'device'] as const;

    if (offerStep) {
      return await trigger(offerTriggerFields);
    }

    if (validateNewSource) {
      if (network === 'xml') {
        return await trigger([...trafficSourceCommonFields, 'xmlSample', 'xmlRedirectField']);
      }

      return await trigger(trafficSourceCommonFields);
    }

    if (validateExistingSource) {
      return await trigger(['selectedTrafficSourceId']);
    }

    if (validateNewCampaign) {
      return await trigger([...campaignTriggerFields, ...campaignOfferSettingsFields]);
    }

    if (validateExistingCampaign) {
      return await trigger(['selectedCampaignId', ...campaignOfferSettingsFields]);
    }

    return true;
  };

  const onSubmit = async (data: WizardFormData) => {
    try {
      const submitData = await prepareWizardSubmissionData(data);
      const result = await submitWizard().call(submitData);

      setSubmissionResult(result);

      const successMessage = result.message || 'Wizard completed successfully!';

      showMessage({
        message: successMessage,
        severity: 'success',
      });

      if (onNavigateToSuccess) {
        onNavigateToSuccess();
      }
    } catch (error: any) {
      console.error('Error submitting wizard:', error);

      const errorMessage = getErrorMessage(error);

      showMessage({
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const prepareWizardSubmissionData = async (data: WizardFormData): Promise<any> => {
    const submitData: any = { ...data };

    submitData.x1Parameters = submitData.x1Parameters.map((param: any) => ({
      id: v4(),
      tag: param.tag,
      autogenerateRule: param.autogenerateRule,
      length: param.length,
      prefix: param.prefix || '',
      replacementMethod: 'dynamicParameter',
      defaultValue: param.defaultValue || 'NONE',
      groupBy: '',
      shareValuesWith: '',
      conditionRegex: null,
    }));

    if (data.campaignIronDomeFile) {
      const campaignIronDome = await parseIronDomeFile(data.campaignIronDomeFile);
      submitData.campaignIronDomeFile = campaignIronDome;
    }

    if (data.trafficSourceIronDomeFile) {
      const trafficSourceIronDome = await parseIronDomeFile(data.trafficSourceIronDomeFile);
      submitData.trafficSourceIronDomeFile = trafficSourceIronDome;
    }

    return submitData;
  };

  const parseIronDomeFile = async (file: File): Promise<{ csvData: any[] } | null> => {
    const { parseIronDomeCSV } = await import('../utils/parseIronDomeCSV');
    try {
      return await parseIronDomeCSV(file);
    } catch (error) {
      console.error(`Error parsing Iron Dome file:`, error);
      return null;
    }
  };

  const getErrorMessage = (error: any): string => {
    const responseError = error.response?.data?.error;
    if (responseError) {
      return responseError;
    }

    const messageError = error.message;
    if (messageError) {
      return messageError;
    }

    return 'Failed to complete wizard. Please try again.';
  };

  const resetWizard = () => {
    form.reset();
    setSubmissionResult(null);
    setValue('offerUniqueId', v4());
  };

  return {
    form,
    validateStep,
    onSubmit,
    submissionResult,
    resetWizard,
    getRedirectUrl,
  };
}
